<template>
	<Teleport to="body">
		<Transition name="backdrop-fade">
			<div v-if="modelValue" class="sidepanel-backdrop" :class="{ fullscreen: isFullscreen }" @click="handleBackdropClick">
				<Transition :name="slideTransition">
					<div v-if="modelValue" class="sidepanel" :class="sidepanelClasses" @click.stop>
						<!-- Header -->
						<div class="sidepanel-header" v-if="!hideHeader" :style="headerStyle">
							<div class="header-content">
								<slot name="header">
									<h2 v-if="title" class="sidepanel-title">
										{{ title }}
									</h2>
								</slot>
							</div>
							<button v-if="showCloseButton" class="close-button" @click="close" aria-label="Close panel">
								<UiIconX />
							</button>
						</div>
						<button v-if="hideHeader && showCloseButton" class="close-button close-button--floating" @click="close" aria-label="Close panel">
							<UiIconX />
						</button>

						<!-- Content -->
						<div class="sidepanel-content" :class="{ 'no-pad': noPad }">
							<slot></slot>
						</div>

						<!-- Footer -->
						<div class="sidepanel-footer" v-if="$slots.footer && !hideFooter">
							<slot name="footer"></slot>
						</div>
					</div>
				</Transition>
			</div>
		</Transition>
	</Teleport>
</template>

<script lang="ts" setup>
import { usePageScroll } from "../../composables/usePageScroll";
import { useViewportSize } from "../../composables/useViewportSize";
export type SidePanelSize = "small" | "medium" | "large" | "xlarge" | "xxlarge" | "fullscreen";

interface SidePanelProps {
	/** Controls the visibility of the sidepanel */
	modelValue: boolean;
	/** Title to display in the header */
	title?: string;
	/** Size of the sidepanel */
	size?: SidePanelSize;
	/** Make the sidepanel fullscreen */
	fullscreen?: boolean;
	/** Show/hide the close button */
	showCloseButton?: boolean;
	/** Hide the entire header section */
	hideHeader?: boolean;
	hideFooter?: boolean;
	/** Close when clicking the backdrop */
	closeOnBackdropClick?: boolean;
	/** Prevent body scroll when open */
	preventScroll?: boolean;
	/** Remove padding from the sidepanel content */
	noPad?: boolean;
	/** Custom inline styles for the header */
	headerStyle?: Record<string, string>;
	/** Prevent the sidepanel from auto-switching to fullscreen on small viewports */
	forceNonFullscreen?: boolean;
}

const props = withDefaults(defineProps<SidePanelProps>(), {
	size: "medium",
	fullscreen: false,
	showCloseButton: true,
	hideHeader: false,
	closeOnBackdropClick: true,
	preventScroll: true,
});

const emit = defineEmits<{
	"update:modelValue": [value: boolean];
	close: [];
	open: [];
}>();

const { lockScroll, unlockScroll } = usePageScroll();
const { width: viewportWidth } = useViewportSize();

const isFullscreen = computed(() => {
	if (props.forceNonFullscreen) return false;
	return props.fullscreen || props.size === "fullscreen" || viewportWidth.value <= 768;
});

const sidepanelClasses = computed(() => ({
	[`sidepanel--${props.size}`]: props.size && !isFullscreen.value,
	"sidepanel--fullscreen": isFullscreen.value,
}));

const slideTransition = computed(() => {
	return isFullscreen.value ? "slide-up" : "slide-left";
});

const close = () => {
	emit("update:modelValue", false);
	emit("close");
};

const handleBackdropClick = () => {
	if (props.closeOnBackdropClick) {
		close();
	}
};

// Handle scroll locking
watch(
	() => props.modelValue,
	(isOpen) => {
		if (props.preventScroll) {
			if (isOpen) {
				lockScroll();
				emit("open");
			} else {
				unlockScroll();
			}
		}
	},
	{ immediate: true },
);

// Clean up on unmount
onUnmounted(() => {
	if (props.preventScroll && props.modelValue) {
		unlockScroll();
	}
});

// Expose close method for parent components
defineExpose({
	close,
});
</script>

<style lang="scss" scoped>
.sidepanel-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.381);
	backdrop-filter: blur(2px);
	z-index: 1000;
	display: flex;
	justify-content: flex-end;
	align-items: stretch;

	&.fullscreen {
		justify-content: flex-start;
		align-items: stretch;
	}
}

.sidepanel {
	background-color: var(--background-25);
	display: flex;
	flex-direction: column;
	max-height: 100%;
	overflow: hidden;
	position: relative;
	border-left: 1px solid var(--background-300);

	// Size variants
	&.sidepanel--small {
		width: 320px;
		max-width: 90vw;
	}

	&.sidepanel--medium {
		width: 480px;
		max-width: 90vw;
	}

	&.sidepanel--large {
		width: 640px;
		max-width: 90vw;
	}

	&.sidepanel--xlarge {
		width: 1000px;
		max-width: 90vw;
	}

	&.sidepanel--xxlarge {
		width: 1200px;
		max-width: 90vw;
	}

	&.sidepanel--fullscreen {
		width: 100%;
		height: 100%;
		max-width: 100%;
		box-shadow: none;
		border-left: none;
	}
}

.close-button--floating {
	position: absolute;
	top: 0.5rem;
	right: 1rem;
	z-index: 1;
	background: var(--background-100);
	border: none;
	padding: 0.5rem;
	cursor: pointer;
	color: var(--text-50);
	transition:
		color 0.2s ease,
		background-color 0.2s ease;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		color: var(--text-50);
		background-color: var(--background-300);
	}

	&:active {
		background-color: var(--background-50);
	}

	:deep(svg) {
		width: 20px;
		height: 20px;
	}
}

.sidepanel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 1.5rem;
	border-bottom: 1px solid var(--background-200);
	flex-shrink: 0;

	.header-content {
		flex: 1;
		min-width: 0;
		font-family: var(--default-font-family);
	}

	.sidepanel-title {
		margin: 0;
		font-size: 1.25rem;
		font-weight: 600;
		color: var(--text-50);
		font-family: var(--default-font-stack);
	}

	.close-button {
		background: transparent;
		border: none;
		padding: 0.5rem;
		cursor: pointer;
		color: var(--text-50);
		transition:
			color 0.2s ease,
			background-color 0.2s ease;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-left: 1rem;
		flex-shrink: 0;
		background-color: var(--background-200);

		&:hover {
			color: var(--text-50);
			background-color: var(--background-300);
		}

		&:active {
			background-color: var(--background-50);
		}

		:deep(svg) {
			width: 20px;
			height: 20px;
		}
	}
}

.sidepanel-content {
	flex: 1;
	overflow-y: auto;
	padding: 1.5rem;

	:deep() {
		article,
		.panel {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			gap: 1rem;
			height: 100%;
		}

		section.main,
		.main {
			display: flex;
			flex-direction: column;
			gap: 1.5rem;
			overflow-y: auto;
			padding: 1.5rem;
			padding-bottom: 6rem;
			margin: -1.5rem;

			margin-bottom: 0.5rem;
			height: 100%;
		}

		section.footer,
		.footer {
			display: flex;
			justify-content: flex-end;
			margin: -1.5rem;
			padding: 0.8rem 1rem;
			position: sticky;
			bottom: -1.5rem;
			background-color: var(--background-25);
			border-top: 1px solid var(--background-300);
			button {
				height: var(--ls-input-height);
			}
		}
	}
}
.sidepanel-content.no-pad {
	padding: 0;
}

.sidepanel-footer {
	padding: 1.5rem;
	flex-shrink: 0;
	display: flex;
	gap: 0.75rem;
	justify-content: flex-end;
}

// Transitions
.backdrop-fade-enter-active,
.backdrop-fade-leave-active {
	transition: opacity 0.3s ease;
}

.backdrop-fade-enter-from,
.backdrop-fade-leave-to {
	opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
	transition:
		transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		opacity 0.3s ease;
}

.slide-left-enter-from,
.slide-left-leave-to {
	transform: translateX(100%);
	opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
	transition:
		transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
		opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
	transform: scale(0.95);
	opacity: 0;
}

// Responsive
@media (max-width: 768px) {
	.sidepanel {
		&.sidepanel--small,
		&.sidepanel--medium,
		&.sidepanel--large,
		&.sidepanel--xlarge {
			width: 100vw;
			max-width: 100vw;
		}
	}
}

@media (prefers-color-scheme: dark) {
	.sidepanel {
		border-left-color: var(--background-100);
	}

	.sidepanel-header {
		border-bottom-color: var(--background-100);
	}

	.sidepanel-content :deep() section.footer {
		border-top-color: var(--background-100);
	}
}

:root[data-theme="dark"] {
	.footer {
		background-color: var(--background-50);
	}

	.sidepanel-content {
		:deep() {
			.footer {
				background-color: var(--background-50);
				border-top-color: var(--background-75);
			}
		}
	}

	.sidepanel {
		background-color: var(--background-25);
	}
}
</style>
